/**
 * 자격증 영문 키를 한글 자격증명으로 매핑하는 유틸리티
 * CBT 페이지에서 한글 자격증명을 우선 표시하기 위해 사용
 * 
 * 새로운 통합 한글 매핑 시스템을 사용합니다.
 */

import { 
  KOREAN_NAME_MAPPING, 
  getKoreanCertificationName,
  getCertificationCategory,
  getCertificationLevel
} from './koreanNameMapper';

// 기존 호환성을 위한 레거시 매핑 테이블 (새 시스템으로 대체됨)
export const EXAM_NAME_MAPPING = KOREAN_NAME_MAPPING;

/**
 * 영문 자격증 키를 한글 자격증명으로 변환 (향상된 버전)
 * @param {string} examKey - 영문 자격증 키 (예: 'electrical-engineer')
 * @returns {string} - 한글 자격증명 (예: '전기기사') 또는 원본 키
 */
export function getKoreanExamName(examKey) {
  return getKoreanCertificationName(examKey);
}

/**
 * 한글 자격증명을 우선으로 하는 시험 제목 생성 (향상된 버전)
 * @param {string} examKey - 영문 자격증 키
 * @param {number} year - 시험 연도
 * @param {number} session - 시험 회차
 * @param {string} originalTitle - 원본 제목 (폴백용)
 * @returns {string} - "전기기사 2024 1회" 형식의 제목
 */
export function generateKoreanExamTitle(examKey, year, session, originalTitle = '') {
  const koreanName = getKoreanCertificationName(examKey);
  
  // 항상 한글명 우선 사용 (새 시스템이 더 포괄적이므로)
  if (koreanName && koreanName !== examKey) {
    return `${koreanName} ${year} ${session}회`;
  }
  
  // 매핑되지 않은 경우 원본 제목 사용 (폴백)
  if (originalTitle) {
    return originalTitle;
  }
  
  // 최종 폴백: 원본 키 사용
  return `${examKey} ${year} ${session}회`;
}

/**
 * 사용 가능한 모든 자격증 목록 반환 (향상된 버전)
 * @returns {Array<{key: string, korean: string, category: string, level: string}>} - 자격증 정보 배열
 */
export function getAllExamMappings() {
  return Object.entries(EXAM_NAME_MAPPING).map(([key, korean]) => ({
    key,
    korean,
    category: getCertificationCategory(key),
    level: getCertificationLevel(key)
  }));
}

/**
 * 자격증 분류별 목록 반환
 * @returns {Object} - 분류별로 그룹화된 자격증 목록
 */
export function getExamsByCategory() {
  const examList = getAllExamMappings();
  const groupedByCategory = {};
  
  examList.forEach(exam => {
    const category = exam.category || '기타';
    if (!groupedByCategory[category]) {
      groupedByCategory[category] = [];
    }
    groupedByCategory[category].push(exam);
  });
  
  return groupedByCategory;
}

/**
 * 자격증 레벨별 목록 반환
 * @returns {Object} - 레벨별로 그룹화된 자격증 목록
 */
export function getExamsByLevel() {
  const examList = getAllExamMappings();
  const groupedByLevel = {};
  
  examList.forEach(exam => {
    const level = exam.level || '기타';
    if (!groupedByLevel[level]) {
      groupedByLevel[level] = [];
    }
    groupedByLevel[level].push(exam);
  });
  
  return groupedByLevel;
}

// 새로운 기능들을 추가로 export
export { getCertificationCategory, getCertificationLevel };

const examNameMapping = {
  EXAM_NAME_MAPPING,
  getKoreanExamName,
  generateKoreanExamTitle,
  getAllExamMappings,
  getExamsByCategory,
  getExamsByLevel,
  getCertificationCategory,
  getCertificationLevel
};

export default examNameMapping;